// Public activity share (signed link + share images). Mirrors
// packages/api/src/lib/activity-share.ts and the /share/activity routes so
// web and mobile share one contract.

export type ActivityShareFormat = "story" | "feed" | "square" | "landscape";
/** brand = Atlas ink canvas behind the route; transparent = white-ink
 * overlay for dark photos; light = dark-ink overlay for pale photos. */
export type ActivityShareStyle = "brand" | "transparent" | "light";

export interface ActivityShareLayout {
  width: number;
  height: number;
  /** Where the route sits inside the image (px). The map-tiles variant fits the
   * map to this box so the route never runs under the text blocks. */
  routeBox: { x: number; y: number; width: number; height: number };
}

// SYNC: mirrors ACTIVITY_CARD_LAYOUT in packages/api/src/lib/activity-card-renderer.ts
// (the API package doesn't depend on @atlas/shared). Change both together.
export const ACTIVITY_SHARE_LAYOUT: Record<ActivityShareFormat, ActivityShareLayout> = {
  story: { width: 1080, height: 1920, routeBox: { x: 64, y: 400, width: 952, height: 900 } },
  feed: { width: 1080, height: 1350, routeBox: { x: 64, y: 400, width: 952, height: 430 } },
  square: { width: 1080, height: 1080, routeBox: { x: 56, y: 300, width: 968, height: 400 } },
  landscape: { width: 1200, height: 630, routeBox: { x: 48, y: 48, width: 540, height: 534 } },
};

export const ACTIVITY_SHARE_SIZES: Record<ActivityShareFormat, { width: number; height: number }> = {
  story: { width: 1080, height: 1920 },
  feed: { width: 1080, height: 1350 },
  square: { width: 1080, height: 1080 },
  landscape: { width: 1200, height: 630 },
};

export interface ActivitySharePeak {
  id: string;
  name: string;
  slug: string;
  elevationM: number | null;
  latitude: number;
  longitude: number;
  /** This activity first collected it; false = a repeat visit (spec 28). */
  isNew?: boolean;
}

export interface ActivityShareLandmark {
  id: string;
  name: string;
  slug: string;
  category: string;
  /** Set when a trig pillar sits on a summit - it collapses into that peak's
   * badged marker (as on the map) rather than getting its own pin. */
  peakId: string | null;
  latitude: number;
  longitude: number;
  isNew?: boolean;
}

export interface ActivityShareTrail {
  id: string;
  name: string;
  slug: string;
  sectionsCount: number;
}

export interface ActivityShareOwner {
  name: string;
  username: string | null;
  level: number;
  avatarUrl: string | null;
}

export interface ActivityShareData {
  activity: {
    id: string;
    name: string;
    startedAt: string;
    sourceType: string;
    sourceId: string | null;
    distanceM: number | null;
    elevationGainM: number | null;
    durationSeconds: number | null;
    geojson: { type: "LineString"; coordinates: [number, number][] } | null;
  };
  peaks: ActivitySharePeak[];
  landmarks: ActivityShareLandmark[];
  trails: ActivityShareTrail[];
  /** Present only when the sharer chose to show their identity. */
  owner: ActivityShareOwner | null;
}

export interface ActivityShareUrlResponse {
  /** Public landing URL (API origin: OG tags for crawlers, redirects browsers
   * to the page). `null` for Strava-sourced activities, which have no public
   * link (Strava API Policy 6.1) - only the image export. */
  url: string | null;
  /** Interactive page URL on the web app. `null` when `url` is. */
  pageUrl: string | null;
  /** PNG endpoint base - append `&format=` and `&style=` (and `&route=0`). */
  imageUrl: string;
}
